import styled from "styled-components";

export const Container = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
`;

export const TablePrimaryActionButton = styled.button`
  min-width: 219px;
  min-height: 38px;
  padding: 8px 20px;
  border-radius: 6px;
  border: 1.5px solid ${({ theme }) => theme.buttons.style1.bg};
  background: ${({ theme }) => theme.buttons.style1.bg};
  color: ${({ theme }) => theme.buttons.style1.text};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: Inter, sans-serif;
  font-size: 16px;
  line-height: 22px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.buttons.style1.hover};
    border-color: ${({ theme }) => theme.buttons.style1.hover};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.buttons.style1.bg};
    outline-offset: 2px;
  }

  & i {
    font-size: 14px;
  }

  @media (max-width: 768px) {
    width: 100%;
    min-width: 0;
  }
`;

export const FormScreen = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 4px;
`;

export const StepScreen = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
`;

export const StepHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 4px;
`;

export const HeaderTop = styled.div<{ $expanded?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $expanded }) => ($expanded ? "16px" : "0")};
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

export const StepContent = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

export const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  width: fit-content;
  padding: 0;
  border: none;
  background: transparent;
  color: #181818;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 22px;
  cursor: pointer;

  & i {
    font-size: 18px;
  }
`;

export const FormHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const Title = styled.h1`
  color: #181818;
  font-family: Inter, sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
`;

export const Subtitle = styled.p`
  color: #181818;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

export const SelectionHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
`;

export const SelectionHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

export const DateRangeFilter = styled.div`
  display: inline-flex;
  align-items: flex-start;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap;
  margin-left: auto;

  @media (max-width: 960px) {
    width: 100%;
    justify-content: flex-start;
    margin-left: 0;
  }
`;

export const DateRangeLabel = styled.span`
  color: #181818;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  padding-top: 9px;
`;

export const DateRangeField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 140px;
  min-height: 60px;

  @media (max-width: 520px) {
    width: 100%;
  }
`;

export const DateRangeInputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

export const DateRangeInput = styled.input<{
  $invalid?: boolean;
  $hasClearButton?: boolean;
}>`
  box-sizing: border-box;
  width: 100%;
  height: 38px;
  padding: 8px ${({ $hasClearButton }) => ($hasClearButton ? "36px" : "12px")}
    8px 12px;
  border-radius: 8px;
  border: 1px solid
    ${({ $invalid, theme }) =>
      $invalid
        ? theme.mode === "dark"
          ? "#ff5c5c"
          : "#d14343"
        : theme.searchPatients.controls.tabOutline};
  background-color: ${({ theme }) => theme.searchPatients.input.bg};
  color: ${({ theme }) => theme.searchPatients.input.text};
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;

  &:focus {
    outline: none;
    border-color: ${({ $invalid, theme }) =>
      $invalid
        ? theme.mode === "dark"
          ? "#ff5c5c"
          : "#d14343"
        : theme.colors.gray};
    box-shadow: ${({ $invalid, theme }) =>
      $invalid
        ? theme.mode === "dark"
          ? "0 0 0 2px rgba(255, 92, 92, 0.18)"
          : "0 0 0 2px rgba(209, 67, 67, 0.12)"
        : "none"};
  }

  &::placeholder {
    color: #9fa0a4;
  }
`;

export const DateRangeClearButton = styled.button`
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  background: transparent;
  color: #7c7f7e;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #181818;
  }

  &:focus-visible {
    outline: none;
    color: #181818;
  }

  & i {
    font-size: 12px;
    font-weight: 700;
  }
`;

export const DateRangeErrorText = styled.div`
  color: ${({ theme }) =>
    theme.mode === "dark" ? "#ff5c5c" : theme.changePassword.invalidText};
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
`;

export const DraftBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 38px;
  padding: 8px 20px;
  border: 1px solid #e7e7e7;
  border-radius: 999px;
  background: #ffffff;
  color: #181818;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;

  &::before {
    content: "";
    width: 12px;
    height: 12px;
    border-radius: 999px;
    background: #e6bd1a;
  }
`;

export const RequestDetailsButton = styled.button<{ $expanded?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  min-height: 38px;
  padding: 8px 20px;
  border: 1px solid #e7e7e7;
  border-radius: 999px;
  background: #ffffff;
  color: #181818;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    color 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    color: #065fa6;
    border-color: #cfd7de;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(6, 95, 166, 0.14);
  }

  & i {
    font-size: 16px;
    transform: rotate(${({ $expanded }) => ($expanded ? "180deg" : "0deg")});
    transition: transform 0.3s ease;
  }
`;

export const RequestDetailsPanel = styled.div<{ $expanded?: boolean }>`
  max-height: ${({ $expanded }) => ($expanded ? "260px" : "0")};
  overflow: hidden;
  opacity: ${({ $expanded }) => ($expanded ? 1 : 0)};
  transition:
    max-height 0.38s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.2s ease;
  pointer-events: ${({ $expanded }) => ($expanded ? "auto" : "none")};
  will-change: max-height, opacity;

  & > div {
    transform: translateY(${({ $expanded }) => ($expanded ? "0" : "-12px")});
    opacity: ${({ $expanded }) => ($expanded ? 1 : 0)};
    transition:
      transform 0.38s cubic-bezier(0.22, 1, 0.36, 1),
      opacity 0.22s ease;
    will-change: transform, opacity;
  }
`;

export const RequestDetailsPanelInner = styled.div`
  border: 1px solid #e7e7e7;
  border-radius: 12px;
  background: #ffffff;
  padding: 28px 28px 24px;
`;

export const RequestMetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 28px 48px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 18px;
  }
`;

export const RequestMetaItem = styled.div`
  display: flex;
  align-items: baseline;
  gap: 16px;
  min-width: 0;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 4px;
  }
`;

export const RequestMetaLabel = styled.div`
  color: #181818;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  flex: 0 0 auto;
`;

export const RequestMetaValue = styled.div`
  color: #181818;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  min-width: 0;
  word-break: break-word;
`;

export const Form = styled.form`
  width: fit-content;
  max-width: 100%;
  margin-top: 16px;
`;

export const Fieldset = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: fit-content;
  max-width: 100%;
`;

export const Label = styled.label`
  color: #181818;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  min-height: 20px;
`;

export const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  width: fit-content;
  max-width: 100%;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
  }
`;

export const RequestIdInput = styled.input`
  box-sizing: border-box;
  flex: 0 0 500px;
  width: 500px;
  height: 38px;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.searchPatients.controls.tabOutline};
  background-color: ${({ theme }) => theme.searchPatients.input.bg};
  color: ${({ theme }) => theme.searchPatients.input.text};
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.gray};
  }

  &::placeholder {
    color: #9fa0a4;
  }

  @media (max-width: 900px) {
    flex-basis: auto;
    width: 100%;
  }
`;

export const ContinueButton = styled.button`
  box-sizing: border-box;
  flex: 0 0 139px;
  width: 139px;
  height: 38px;
  padding: 8px 20px;
  border-radius: 6px;
  border: 1.5px solid ${({ theme }) => theme.buttons.style1.bg};
  background: ${({ theme }) => theme.buttons.style1.bg};
  color: ${({ theme }) => theme.buttons.style1.text};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: Inter, sans-serif;
  font-size: 16px;
  line-height: 22px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.buttons.style1.hover};
    border-color: ${({ theme }) => theme.buttons.style1.hover};
  }

  &:disabled {
    background: ${({ theme }) => theme.buttons.style1.disabledBg};
    border-color: ${({ theme }) => theme.buttons.style1.disabledBg};
    color: ${({ theme }) => theme.buttons.style1.disabledText};
    cursor: not-allowed;
  }

  @media (max-width: 900px) {
    flex-basis: auto;
    width: 100%;
    min-width: 0;
  }
`;

const BaseFooterButton = styled.button`
  box-sizing: border-box;
  padding: 8px 20px;
  border-radius: 6px;
  font-family: Inter, sans-serif;
  font-size: 16px;
  line-height: 22px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;
`;

export const Footer = styled.div`
  padding-top: 24px;
  border-top: 1px solid #e7e7e7;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 1100px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const FooterMeta = styled.div`
  color: #181818;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

export const FooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

export const TooltipAnchor = styled.span`
  display: inline-flex;
`;

export const TooltipTheme = styled.div`
  .roi-footer-tooltip {
    background: #181818 !important;
    color: #ffffff !important;
    border-radius: 6px !important;
    padding: 8px 12px !important;
    font-family: Inter, sans-serif !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    line-height: 20px !important;
    box-shadow: 0 8px 24px rgba(24, 24, 24, 0.14);
    max-width: 240px;
    z-index: 20;
  }
`;

export const SecondaryActionButton = styled(BaseFooterButton)`
  border: 1.5px solid ${({ theme }) => theme.buttons.style1.bg};
  background: #ffffff;
  color: ${({ theme }) => theme.buttons.style1.bg};

  &:hover {
    border-color: ${({ theme }) => theme.buttons.style1.hover};
    color: ${({ theme }) => theme.buttons.style1.hover};
  }

  &:disabled {
    border-color: ${({ theme }) => theme.buttons.style1.disabledBg};
    background: #ffffff;
    color: #7c7f7e;
    cursor: not-allowed;
  }
`;

export const NeutralActionButton = styled(BaseFooterButton)`
  border: 1.5px solid #7c7f7e;
  background: #ffffff;
  color: #7c7f7e;

  &:hover {
    border-color: #5a5959;
    color: #5a5959;
  }

  &:disabled {
    border-color: ${({ theme }) => theme.buttons.style1.disabledBg};
    background: #ffffff;
    color: #7c7f7e;
    cursor: not-allowed;
  }
`;

export const CompleteActionButton = styled(BaseFooterButton)`
  border: 1.5px solid ${({ theme }) => theme.buttons.style1.bg};
  background: ${({ theme }) => theme.buttons.style1.bg};
  color: ${({ theme }) => theme.buttons.style1.text};

  &:hover {
    background: ${({ theme }) => theme.buttons.style1.hover};
    border-color: ${({ theme }) => theme.buttons.style1.hover};
  }

  &:disabled {
    background: ${({ theme }) => theme.buttons.style1.disabledBg};
    border-color: ${({ theme }) => theme.buttons.style1.disabledBg};
    color: ${({ theme }) => theme.buttons.style1.disabledText};
    cursor: not-allowed;
  }
`;
